/**
 * **The edit bar** — one Save for every page that edits something (W28,
 * FR-65, plan D84), copy-adapted from ProDesk's
 * (`repo:prodesk:apps/web/src/components/edit/EditBar.tsx`) without its
 * website preview link.
 *
 * Stuck to the bottom of the content, full content width, **always drawn** on
 * an edit page (a bar that appears only when something changed moves the page
 * under the thumb), carrying:
 *
 * - **Save** — a spinner while saving; disabled, with the reason as its title
 *   and in the state line, when nothing changed, when `invalid` says the page
 *   cannot be sent as it stands, or when `problem` says the last Save was
 *   refused and nothing has changed since.
 * - **The state** — *Unsaved changes*, *Everything is saved*, or the problem in
 *   the destructive tone.
 * - **Discard** — a ghost button, only while something changed and only when
 *   the page gives `onDiscard`.
 *
 * A reader who may not edit (`editable={false}`) sees the bar without Save or
 * Discard and a *View only* line, so the page does not change shape by role.
 */
import { Button } from '@fiveox/ui'
import { CircleAlert, CircleCheck, CircleDot, Eye } from 'lucide-react'

export interface EditBarProps {
  /** Is there anything on the page that Save would send? */
  dirty: boolean
  /** A save is in flight. */
  saving: boolean
  onSave: () => void
  /** Put the page back to what is saved. Discard is shown only when given. */
  onDiscard?: () => void
  /** What the service said of the last Save — shown in place of the state; Save is offered again once something changes. */
  problem?: string | null
  /** What the page itself finds wrong before anything is sent (a field out of range, a label missing) — shown in place of the state, Save refused until it is put right, Discard kept. */
  invalid?: string | null
  /** Defaults to *Save changes*. */
  saveLabel?: string
  /** `false` for a reader without the verb: no Save, no Discard. Default true. */
  editable?: boolean
}

/** The line the bar shows, and the reason Save is disabled — pure, so the states are testable without mounting. */
export function editBarState({ dirty, problem = null, invalid = null, editable = true }: Pick<EditBarProps, 'dirty' | 'problem' | 'invalid' | 'editable'>): {
  tone: 'problem' | 'dirty' | 'saved' | 'readonly'
  text: string
  /** Why Save is disabled, or `null` when it may be pressed. */
  refused: string | null
} {
  if (!editable) return { tone: 'readonly', text: 'View only — only a ProDoc administrator can change these.', refused: 'View only' }
  // The page's own check comes first: nothing is sent until it is put right.
  if (invalid) return { tone: 'problem', text: invalid, refused: invalid }
  // A refusal after a Save is said here, and the page may be saved again once it is put right.
  if (problem) return { tone: 'problem', text: problem, refused: dirty ? null : problem }
  if (dirty) return { tone: 'dirty', text: 'Unsaved changes', refused: null }
  return { tone: 'saved', text: 'Everything is saved', refused: 'Nothing has changed yet' }
}

export function EditBar({ dirty, saving, onSave, onDiscard, problem = null, invalid = null, saveLabel = 'Save changes', editable = true }: EditBarProps) {
  const state = editBarState({ dirty, problem, invalid, editable })
  return (
    <div data-edit-bar="" data-state={state.tone} className="border-border bg-background/95 sticky bottom-0 z-30 mt-2 border-t py-3 backdrop-blur">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {editable && (
          <Button onClick={onSave} loading={saving} loadingText="Saving…" disabled={saving || state.refused !== null} title={state.refused ?? undefined}>
            {saveLabel}
          </Button>
        )}
        {editable && dirty && onDiscard && (
          <Button variant="ghost" onClick={onDiscard} disabled={saving}>
            Discard
          </Button>
        )}
        <p role="status" aria-live="polite" className={`flex min-w-0 items-center gap-1.5 text-sm ${state.tone === 'problem' ? 'text-destructive' : 'text-muted-foreground'}`}>
          {state.tone === 'problem' ? (
            <CircleAlert size={14} className="shrink-0" aria-hidden />
          ) : state.tone === 'dirty' ? (
            <CircleDot size={14} className="text-primary shrink-0" aria-hidden />
          ) : state.tone === 'saved' ? (
            <CircleCheck size={14} className="text-primary shrink-0" aria-hidden />
          ) : (
            <Eye size={14} className="shrink-0" aria-hidden />
          )}
          <span className="min-w-0">{state.text}</span>
        </p>
      </div>
    </div>
  )
}
